import { Link, useHistory, useLocation } from "#src/components/Router.tsx";
import { BorderButton } from "#src/components/buttons/BorderButton.tsx";
import LinkButton from "#src/components/buttons/LinkButton.tsx";
import FacebookIcon from "#src/components/icons/FacebookIcon.tsx";
import InstagramIcon from "#src/components/icons/InstagramIcon.tsx";
import TwitterIcon from "#src/components/icons/TwitterIcon.tsx";
import { TerminalBanner } from "#src/components/items/TerminalBanner.tsx";
import { ProfileSwitcher } from "#src/components/menu/ProfileSwitcher.tsx";
import { ContactSupportModal } from "#src/components/modals/ContactSupportModal.tsx";
import { UserReferralTemplate } from "#src/components/referrals/UserReferralTemplate.tsx";
import UserModel from "#src/models/UserModel.ts";
import { useSession } from "#src/stores/Session.tsx";
import { useStore } from "#src/stores/Store.tsx";
import { useUI } from "#src/stores/UI.tsx";
import colors from "#src/utils/colors.ts";
import { formatPhoneNumber } from "#src/utils/formatPhone.ts";
import { modals } from "#src/utils/modals.ts";
import tw from "#src/utils/tw.ts";
import {
  BetaAccessType,
  Level,
  PermissionType,
} from "#types/code-generator.ts";
import { DepartmentFilter } from "./DepartmentFilter";
import { MenuContainer } from "./MenuContainer";
import { MenuItem } from "./MenuItem";

interface Props {}

export default function Menu(_props: Props) {
  const ui = useUI();
  const store = useStore();
  const session = useSession();
  const history = useHistory();
  const location = useLocation();

  function toggleMenu() {
    ui.toggleMenu();
  }

  function signOut() {
    store.signOut();
    session.setDepartments([]);
    session.clearSession();
    ui.setMenuOpen(false);

    setTimeout(() => {
      history.push("/logout");
    }, 150);
  }

  function switchView(level: Level) {
    const newUser = new UserModel({
      ...store.user,
      level,
    });

    store.setCurrentUser({
      user: newUser,
    });

    if (level === Level.Admin && location.pathname.startsWith("/account")) {
      history.push("/admin");
    }
  }

  function emailSupport() {
    modals.show(ContactSupportModal, {
      sendToSupport: true,
    });
  }

  function deleteAccount() {
    modals.show(ContactSupportModal, {
      title: "Delete Account",
      deleteAccount: true,
    });
  }

  function emailOrg() {
    modals.show(ContactSupportModal, {
      title: `Send ${store.organization?.title} a Message`,
    });
  }

  let menuFooter;
  const hasMeets = store.organization?.betaAccess.includes(
    BetaAccessType.Meets
  );
  const hasMeetAccess =
    !store.organization?.hasDepartments ||
    session.departments.some((d) => d.meetAccess);

  let menuContent = (
    <>
      <MenuItem to="/">Home</MenuItem>
      {!store.user && (
        <>
          <MenuItem to="/login">Sign In</MenuItem>
          <MenuItem to="/register">Create Your Account</MenuItem>
        </>
      )}
    </>
  );

  if (
    store.user?.level === Level.Admin ||
    store.user?.level === Level.SuperAdmin ||
    store.user?.level === Level.Sales
  ) {
    const hasVolunteers = store.organization.betaAccess.includes(
      BetaAccessType.Volunteers
    );
    menuContent = (
      <>
        <MenuItem to="/admin">Home</MenuItem>
        <MenuItem to="/admin/schedule">Today</MenuItem>
        <MenuItem to="/admin/users">People</MenuItem>

        {store.organization?.betaAccess.includes(
          BetaAccessType.Programming
        ) && <MenuItem to={"/admin/group-offerings"}>Programming</MenuItem>}

        {store.organization?.betaAccess.includes(
          BetaAccessType.PrivateLessons
        ) && (
          <MenuItem to="/admin/custom-private-lessons">
            Custom {store.organization?.privateTerminology}
          </MenuItem>
        )}

        {store.organization?.betaAccess.includes(
          BetaAccessType.Memberships
        ) && (
          <MenuItem to="/admin/membership-offerings">
            <div className="flex items-center">
              <div className="flex-1">Memberships</div>
            </div>
          </MenuItem>
        )}

        {hasMeets && hasMeetAccess && (
          <MenuItem to="/admin/meets">Meets & Times</MenuItem>
        )}

        {hasVolunteers && (
          <MenuItem to="/admin/volunteers">Volunteers</MenuItem>
        )}

        {(store.user?.permissions.includes(
          PermissionType.AccessAdvancedSetting
        ) ||
          store.user?.isSalesOrAbove) && (
          <MenuItem to="/admin/settings">Settings & Reports</MenuItem>
        )}
      </>
    );
    menuFooter = (
      <div>
        <div>Switch to my:</div>
        <LinkButton
          className="block"
          onClick={() => switchView(Level.Instructor)}
        >
          <strong>{store.organization?.staffTerminology} view</strong>
        </LinkButton>

        <LinkButton
          className="block"
          onClick={() => switchView(Level.AccountOwner)}
        >
          <strong>{store.organization?.customerTerminology} view</strong>
        </LinkButton>
      </div>
    );
  }

  if (store.user?.level === Level.Instructor) {
    menuContent = (
      <>
        {store.organization?.hasDepartments &&
          store.user.departments.length > 1 && <DepartmentFilter />}
        <MenuItem to="/account">Home</MenuItem>
        <MenuItem to="/admin/schedule">Today</MenuItem>

        {store.organization?.betaAccess.includes(
          BetaAccessType.PrivateLessons
        ) && (
          <MenuItem to="/admin/custom-private-lessons">
            Custom {store.organization?.privateTerminology}
          </MenuItem>
        )}

        {hasMeets && hasMeetAccess && (
          <MenuItem to="/admin/meets">Meets & Times</MenuItem>
        )}

        <MenuItem to="/account/upcoming-schedule">
          My Upcoming Schedule
        </MenuItem>
        <MenuItem to="/account/settings">My Account</MenuItem>
      </>
    );
    menuFooter = (
      <div>
        <div>Switch to my:</div>

        {(store.userLevel === Level.Admin ||
          store.userLevel === Level.SuperAdmin ||
          store.userLevel === Level.Sales) && (
          <LinkButton className="block" onClick={() => switchView(Level.Admin)}>
            <strong>Admin view</strong>
          </LinkButton>
        )}

        <LinkButton
          className="block"
          onClick={() => switchView(Level.AccountOwner)}
        >
          <strong>{store.organization?.customerTerminology} view</strong>
        </LinkButton>
      </div>
    );
  }

  if (store.user?.level === Level.AccountOwner) {
    menuContent = (
      <>
        <MenuItem to="/account">Home</MenuItem>
        <MenuItem to="/account/settings">My Account</MenuItem>
      </>
    );

    menuFooter = (
      <div>
        {(store.userLevel === Level.Instructor ||
          store.userLevel === Level.Admin ||
          store.userLevel === Level.SuperAdmin ||
          store.userLevel === Level.Sales) && (
          <>
            <div>Switch to my:</div>

            {(store.userLevel === Level.Admin ||
              store.userLevel === Level.SuperAdmin ||
              store.userLevel === Level.Sales) && (
              <>
                <LinkButton
                  className="block"
                  onClick={() => switchView(Level.Admin)}
                >
                  <strong>
                    {store.organization?.administratorTerminology} view
                  </strong>
                </LinkButton>

                <LinkButton
                  className="block"
                  onClick={() => switchView(Level.Instructor)}
                >
                  <strong>{store.organization?.staffTerminology} view</strong>
                </LinkButton>
              </>
            )}

            {store.userLevel === Level.Instructor && (
              <LinkButton
                className="block"
                onClick={() => switchView(Level.Instructor)}
              >
                <strong>{store.organization?.staffTerminology} view</strong>
              </LinkButton>
            )}
          </>
        )}
      </div>
    );
  }

  if (store.user?.level === Level.Captyn) {
    menuContent = (
      <>
        <MenuItem to="/account">Home</MenuItem>
      </>
    );
  }

  return (
    <>
      <MenuContainer>
        <div className="flex h-full flex-col">
          <div className="flex-1">
            <ProfileSwitcher />

            {store.organization?.hasDepartments &&
              ((store.user?.isAdminOrAbove &&
                store.user?.departments.length > 1) ||
                store.user?.isSalesOrAbove) && <DepartmentFilter />}

            <div className="border-borderGray border-b bg-white">
              {menuContent}
            </div>

            {store.user && (
              <div className="px-4 py-4">
                <BorderButton
                  bgColor={colors.lightGray}
                  onClick={signOut}
                  className="w-full"
                >
                  Sign Out
                </BorderButton>
              </div>
            )}

            {(store.user?.level === Level.AccountOwner || !store.user) && (
              <>
                <div className="border-borderGray mx-4 -mt-px mb-4 border-t pt-4">
                  <div className="step-subhead mb-2">
                    Contact {store.organization?.title}
                  </div>

                  <div className="mb-3">
                    {store.organization?.email && (
                      <LinkButton onClick={emailOrg} className="mb-1 block">
                        <i className="far fa-envelope w-5 text-left" /> Send a
                        message
                      </LinkButton>
                    )}

                    {store.organization?.phone && (
                      <a
                        href={`tel:${formatPhoneNumber(
                          store.organization?.phone
                        )}`}
                        className="text-organization block"
                      >
                        <i className="far fa-phone w-5 text-left" />{" "}
                        {formatPhoneNumber(store.organization?.phone)}
                      </a>
                    )}
                  </div>

                  <div className="flex gap-3">
                    {store.organization?.facebook && (
                      <a
                        href={
                          store.organization?.facebook.startsWith("http")
                            ? store.organization?.facebook
                            : `https://${store.organization?.facebook}`
                        }
                        rel="nofollow noreferrer"
                        target="_blank"
                        className="text-organization transition hover:opacity-80"
                      >
                        <FacebookIcon className="h-6 w-6" />
                      </a>
                    )}

                    {store.organization?.instagram && (
                      <a
                        href={
                          store.organization?.instagram.startsWith("http")
                            ? store.organization?.instagram
                            : `https://${store.organization?.instagram}`
                        }
                        rel="nofollow noreferrer"
                        target="_blank"
                        className="text-organization transition hover:opacity-80"
                      >
                        <InstagramIcon className="h-6 w-6" />
                      </a>
                    )}

                    {store.organization?.twitter && (
                      <a
                        href={
                          store.organization?.twitter.startsWith("http")
                            ? store.organization?.twitter
                            : `https://${store.organization?.twitter}`
                        }
                        rel="nofollow noreferrer"
                        target="_blank"
                        style={{
                          color: store.theme.orgColor,
                        }}
                        className="transition hover:opacity-80"
                      >
                        <TwitterIcon className="h-6 w-6" />
                      </a>
                    )}
                  </div>
                </div>
              </>
            )}

            {store.user && (
              <div className="border-borderGray mx-4 border-t py-4">
                {menuFooter}
                {(store.userLevel === Level.SuperAdmin ||
                  store.userLevel === Level.Sales) &&
                  store.user?.level !== store.userLevel && (
                    <LinkButton
                      className="block"
                      onClick={() => switchView(store.userLevel)}
                    >
                      <strong>Reset view</strong>
                    </LinkButton>
                  )}
              </div>
            )}

            {store.user && (
              <div className="mb-8 p-4">
                <UserReferralTemplate small />

                <TerminalBanner menu />
              </div>
            )}
          </div>

          <div className="text-empty px-4 pt-4 pb-12 text-xs">
            <a
              href="https://captyn.com"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-empty block"
            >
              <strong>Powered by</strong>{" "}
              <img
                src="/img/captyn-logo-gray.svg"
                alt="Captyn"
                className="mt-0.5 ml-0.5 h-3.5"
              />
            </a>
            <Link to="/terms-of-service" className="text-empty">
              Terms of Service
            </Link>{" "}
            •{" "}
            <Link to="/privacy-policy" className="text-empty">
              Privacy Policy
            </Link>
            <div>
              <button className="text-empty" onClick={emailSupport}>
                Contact
              </button>{" "}
              •{" "}
              <button className="text-empty" onClick={deleteAccount}>
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </MenuContainer>
      <div
        className={tw(
          "fixed inset-0 z-20 transition",
          ui.menuOpen
            ? "bg-dark opacity-60"
            : "pointer-events-none bg-transparent opacity-0"
        )}
        onClick={toggleMenu}
      />
    </>
  );
}
